import './TemplateEditorBox.scss'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { Col } from 'react-bootstrap'
import { $generateHtmlFromNodes } from '@lexical/html'
import { type LexicalEditor } from 'lexical'
import { RichTextToolbarPlugin } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { LEXICAL_NODES } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/nodes'
import { WritableLexicalEditor } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import { getEditorStateFromHtml } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/utils'
import { Button } from '../../../components/Button/Button'
import { type TemplateType, type TemplateValue } from '../../../models/TemplateLibrary'
import { useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { UnsavedChangesModal } from '../../../components/Modal/UnsavedChangesModal'
import { useAccount } from '../../../common/useAccount'
import { useUnsavedChangesGuard } from '../../../common/useUnsavedChangesGuard'
import {
  disabledBannerTextFor,
  isOwnedByAnotherUser,
  OWNED_BY_ANOTHER_USER_TEXT,
  templateSaveNotices,
  UNOPENABLE_TEMPLATE_TEXT,
} from './templateEditorMessages'
import { useIntercom } from 'react-use-intercom'
import { SaveBarFrame } from '../../../components/SaveBarNotice/SaveBarFrame'
import { type SaveBarNotice, withoutSuccess } from '../../../components/SaveBarNotice/saveBarNotice'

export interface TemplateEditorBoxProps {
  templateValue: TemplateValue
  shouldReset: boolean
  readOnly?: boolean
  showDisabledBanner?: boolean
  onSave?: (template: TemplateType) => Promise<void> | void
  onRequestDeferredSave?: (template: TemplateType) => void
  onChange?: (newTemplate: string) => void
  loading?: boolean
  bypassBlockerRef?: React.MutableRefObject<boolean>
  hasAIPendingChanges?: boolean
}

interface NormalizedBaseline {
  source: string
  html: string
}

export const TemplateEditorBox: React.FC<TemplateEditorBoxProps> = ({
  templateValue,
  shouldReset,
  readOnly,
  showDisabledBanner,
  onSave,
  onRequestDeferredSave,
  onChange,
  loading,
  bypassBlockerRef,
  hasAIPendingChanges,
}) => {
  const { account } = useAccount()
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const {
    template: { template: storedTemplate },
  } = templateValue
  const template = typeof storedTemplate === 'string' ? storedTemplate : ''
  const editorRef = useRef<LexicalEditor>(null)
  const [currentHtml, setCurrentHtml] = useState<string>(template)
  const [normalized, setNormalized] = useState<NormalizedBaseline | null>(null)
  const [notice, setNotice] = useState<SaveBarNotice | null>(null)
  const disabledBannerText = disabledBannerTextFor(templateValue, account)

  const initialEditorState = useMemo(() => getEditorStateFromHtml(template), [template])

  const hasBaseline = normalized?.source === template
  const baselineHtml = hasBaseline ? normalized.html : template
  const isDirty = !readOnly && (!!hasAIPendingChanges || currentHtml !== baselineHtml)

  const resetToInitialTemplate = useCallback(() => {
    const editor = editorRef.current
    if (!editor) {
      return
    }
    editor.setEditorState(initialEditorState)
  }, [initialEditorState])

  useEffect(() => {
    if (shouldReset) {
      resetToInitialTemplate()
    }
  }, [shouldReset, resetToInitialTemplate])

  useEffect(() => {
    resetToInitialTemplate()
  }, [resetToInitialTemplate])

  useEffect(() => {
    editorRef.current?.setEditable(!readOnly)
  }, [readOnly])

  const handleDiscard = useCallback(() => {
    resetToInitialTemplate()
    setNotice(null)
  }, [resetToInitialTemplate])

  const handleChange = () => {
    const editor = editorRef.current
    if (!editor) {
      return
    }
    const editorState = editor.getEditorState()
    editorState.read(() => {
      const newHtml = $generateHtmlFromNodes(editor, null)
      if (hasBaseline && newHtml !== normalized.html) {
        setNotice(withoutSuccess)
      }
      setNormalized((previous) => (previous?.source === template ? previous : { source: template, html: newHtml }))
      setCurrentHtml(newHtml)
      onChange?.(newHtml)
    })
  }

  const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesGuard(isDirty, false, bypassBlockerRef)

  const proceedBlocker = async (save: boolean) => {
    if (save) {
      try {
        await onSave?.({ template: currentHtml })
      } catch (error) {
        setNotice(templateSaveNotices.saveFailed(error))
        cancelLeave()
        return
      }
    }
    confirmLeave()
  }

  const handleSave = useCallback(async () => {
    if (templateValue.scope === 'system' && onRequestDeferredSave) {
      onRequestDeferredSave({ template: currentHtml })
      return
    }
    if (onSave) {
      try {
        await onSave({ template: currentHtml })
      } catch (error) {
        setNotice(templateSaveNotices.saveFailed(error))
        return
      }
      flushSync(() => {
        setNormalized({ source: template, html: currentHtml })
      })
      setNotice(templateSaveNotices.saved())
    }
  }, [onSave, onRequestDeferredSave, templateValue.scope, currentHtml, template])

  return (
    <>
      <Col className='d-flex flex-column h-100'>
        <div className='template-library-box d-flex flex-column flex-grow-1 h-100'>
          <div className='template-library-dynamic-panel d-flex flex-column flex-grow-1 overflow-hidden'>
            {initialEditorState ? (
              <WritableLexicalEditor
                className='zocks-px-2.0 zocks-py-2.0 template-library-box-editor flex-grow-1 d-flex flex-column overflow-hidden'
                containerClassName='w-100'
                editorContainerClassName='template-library-editor flex-grow-1 px-4 py-3 overflow-y-auto'
                onChange={handleChange}
                initialEditorState={initialEditorState}
                lexicalNodes={LEXICAL_NODES}
                header={
                  !readOnly ? (
                    <RichTextToolbarPlugin className='p-3 gap-2 border border-1 zocks-border-1.0 zocks-border-color-gray-300' />
                  ) : showDisabledBanner ? (
                    <TemplateEditorDisabledBanner text={disabledBannerText} />
                  ) : null
                }
                ref={editorRef}
              />
            ) : null}
          </div>

          {!readOnly ? (
            <TemplateEditorBoxControls
              onDiscard={handleDiscard}
              onSave={handleSave}
              loading={Boolean(loading)}
              disabled={!isDirty}
              notice={isDirty ? withoutSuccess(notice) : notice}
              onDismissNotice={() => setNotice(null)}
              infoText={
                currentHtml.length > 60000
                  ? 'Long template may interrupt generation. Try shortening it.'
                  : isOwnedByAnotherUser(templateValue, currentUserId)
                    ? OWNED_BY_ANOTHER_USER_TEXT
                    : undefined
              }
            />
          ) : null}
        </div>
      </Col>
      {isBlocked ? (
        <UnsavedChangesModal
          show
          onHide={cancelLeave}
          onSave={() => void proceedBlocker(true)}
          onDiscard={() => void proceedBlocker(false)}
          hideSave={templateValue.scope === 'system'}
          loading={loading}
        />
      ) : null}
    </>
  )
}

export interface TemplateEditorBoxControlsProps {
  onDiscard: () => void
  onSave: () => void
  loading: boolean
  disabled?: boolean
  discardDisabled?: boolean
  notice: SaveBarNotice | null
  onDismissNotice: () => void
  infoText?: string
  saveLabel?: string
  statusText?: string
  statusTone?: 'neutral' | 'error'
}

export const TemplateEditorBoxControls: React.FC<TemplateEditorBoxControlsProps> = ({
  onDiscard,
  onSave,
  infoText,
  loading,
  disabled,
  discardDisabled,
  notice,
  onDismissNotice,
  saveLabel = 'Save',
  statusText,
  statusTone = 'neutral',
}) => {
  const { show: showSupport } = useIntercom()
  const neutralText = statusText ?? infoText
  return (
    <SaveBarFrame
      notice={notice}
      onDismissNotice={onDismissNotice}
      onContactSupport={showSupport}
      className='template-editor-box-controls position-sticky start-0 end-0 rounded-4 shadow'
    >
      <div className='template-editor-box-controls__bar py-3 zocks-px-1.75 rounded-4 d-flex justify-content-between align-items-center gap-2'>
        {statusText && statusTone === 'error' ? (
          <div role='status' className='d-flex align-items-center zocks-gap-0.625'>
            <span className='d-flex rounded-circle p-1 bg-white bg-opacity-10'>
              <i className='bi bi-exclamation-circle-fill fs-4 lh-1 zocks-text-danger' />
            </span>
            <span className='paragraph-large zocks-text-white'>{statusText}</span>
          </div>
        ) : neutralText ? (
          <div className='d-flex align-items-center gap-2'>
            <i className='bi bi-info-square zocks-text-gray-500' />
            <span className='zocks-text-gray-500'>{neutralText}</span>
          </div>
        ) : (
          <div />
        )}
        <div className='d-flex gap-2 justify-content-end'>
          <Button variant='darkGhost' onClick={onDiscard} disabled={loading || (discardDisabled ?? disabled)}>
            Discard
          </Button>
          <Button variant='primary' onClick={onSave} loading={loading} disabled={disabled}>
            {saveLabel}
          </Button>
        </div>
      </div>
    </SaveBarFrame>
  )
}

interface TemplateEditorDisabledBannerProps {
  text: string
}

export const TemplateEditorDisabledBanner: React.FC<TemplateEditorDisabledBannerProps> = ({ text }) => {
  return (
    <div className='d-flex align-items-center gap-2 p-3 rounded-4 border zocks-border-color-gray-300 zocks-bg-color-gray-100'>
      <i className='bi bi-info-square zocks-text-gray-900' />
      <span className='zocks-text-gray-900'>{text}</span>
    </div>
  )
}

interface TemplateNoticeBoxProps {
  text: string
  children?: React.ReactNode
}

export const TemplateNoticeBox: React.FC<TemplateNoticeBoxProps> = ({ text, children }) => (
  <Col className='d-flex flex-column h-100'>
    <div className='template-library-box d-flex flex-column flex-grow-1 h-100'>
      <div className='d-flex flex-column flex-grow-1 gap-3 overflow-y-auto zocks-p-2.0'>
        <TemplateEditorDisabledBanner text={text} />
        {children}
      </div>
    </div>
  </Col>
)

export const UnopenableTemplateBox: React.FC = () => <TemplateNoticeBox text={UNOPENABLE_TEMPLATE_TEXT} />
